import { Component, DestroyRef, EventEmitter, Input, OnInit, Output } from '@angular/core';
import {
  API,
  AssetFileDTO,
  CompanyDTO,
  CompanyFullDTO,
  MediumDTO,
  MediumFullDTO,
  MediumGroupDTO,
  MediumGroupFullDTO,
  SharedAssetDTO,
  SharedAssetFullDTO,
} from 'src/app/services/api/api';
import { ModalNavigationComponent } from 'src/app/services/gui/modal/modal-navigation-component';
import { ModalNavigationService } from 'src/app/services/gui/modal/modal-navigation.service';
import { UtilService } from 'src/app/services/util.service';
import { ErrorService } from 'src/app/services/error.service';
import { MsOptions } from 'src/app/services/global/global-interfaces';
import { AssetConversionModalOptions } from './asset-conversion-modal.model';
import { PermissionService } from 'src/app/services/permission.service';
import { UserService } from 'src/app/services/user.service';
import { UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { take } from 'rxjs/operators';

@Component({
  selector: 'asset-conversion-modal',
  templateUrl: './asset-conversion-modal.component.html',
  styleUrls: ['./asset-conversion-modal.component.scss'],
  standalone: false,
})
export class AssetConversionModalComponent implements OnInit, ModalNavigationComponent {
  isInModalContext: boolean;
  blockBackdrop?: boolean;

  conversionOptions: AssetConversionModalOptions;
  msCompanyOptions: MsOptions;

  companies: CompanyDTO[];

  company: CompanyDTO;

  @Output() nameEmitter: EventEmitter<string>;

  private _name: string;

  @Input() set name(val: string) {
    this._name = val;
    if (val) {
      this.updateForm();
    } else {
      this._name = val;
    }
  }

  get name() {
    return this._name;
  }

  form: UntypedFormGroup;

  get Name() {
    return this.form.get('Name');
  }

  constructor(
    private api: API,
    private modalNavigation: ModalNavigationService,
    public permissionService: PermissionService,
    private util: UtilService,
    public errorHandler: ErrorService,
    private userService: UserService,
    private destroyRef: DestroyRef
  ) {
    this.nameEmitter = new EventEmitter<string>();
    this.company = this.userService.user.client.primaryCompany;
    this.form = new UntypedFormGroup({
      Name: new UntypedFormControl(''),
    });
  }

  ngOnInit() {
    if (this.isInModalContext) {
      if (this.modalNavigation.getCurrentNavigation().data) {
        this.conversionOptions = this.modalNavigation.getCurrentNavigation().data;
      }
    }
    this.registerFormChangesListener();
  }

  registerFormChangesListener() {
    this.Name.valueChanges.subscribe(value => {
      this._name = value;
      this.nameEmitter.emit(this.name);
    });
  }

  updateForm() {
    this.Name.setValue(this.name);
    if (this.name) {
      this.Name.markAsTouched();
    }
  }

  msRequest(type: string, options?: MsOptions) {
    if (type === 'COMPANIES') {
      if (options) {
        this.msCompanyOptions = options;
      }
      if (this.msCompanyOptions?.pagination) {
        this.api
          .shared_Company_retrieveAll(
            {
              search: this.util.encodeSearchQueryParam(false, this.msCompanyOptions?.search),
            },
            this.msCompanyOptions?.pagination
          )
          .pipe(takeUntilDestroyed(this.destroyRef), take(1))
          .subscribe({
            next: (comps: CompanyFullDTO[]) => {
              this.companies = comps;
            },
            error: error => {
              this.errorHandler.errorHandler(error);
            },
          });
      }
    }
  }

  update($event: CompanyDTO[], type: string) {
    if (type === 'COMPANIES') {
      if ($event?.length > 0) {
        this.company = $event[0];
      } else {
        this.company = this.userService.user.client.primaryCompany;
      }
    }
  }

  createMediumFromAssetFile(assetFile: AssetFileDTO) {
    const medium: MediumFullDTO = new MediumFullDTO();
    medium.company = this.company;
    medium.name = this.name;
    this.api
      .mm_Medium_createFromAssetFile(assetFile?.id, medium)
      .pipe(takeUntilDestroyed(this.destroyRef), take(1))
      .subscribe({
        next: (res: MediumFullDTO) => {
          this.close(res);
        },
        error: error => {
          this.errorHandler.errorHandler(error);
        },
      });
  }

  createMediumGroupFromAsset(asset: SharedAssetDTO) {
    const mediumGroup: MediumGroupFullDTO = new MediumGroupFullDTO();
    mediumGroup.name = this.name;
    this.api
      .mm_MediumGroup_createFromAsset(this.company?.id, asset?.id, mediumGroup)
      .pipe(takeUntilDestroyed(this.destroyRef), take(1))
      .subscribe({
        next: (res: MediumGroupFullDTO) => {
          this.close(res);
        },
        error: error => {
          this.errorHandler.errorHandler(error);
        },
      });
  }

  ok() {
    switch (this.conversionOptions.mode) {
      case 'ASSET_FILE_TO_MEDIUM':
        return this.createMediumFromAssetFile(this.conversionOptions.obj as AssetFileDTO);
      case 'ASSET_TO_MEDIUM_GROUP':
        return this.createMediumGroupFromAsset(this.conversionOptions.obj as SharedAssetFullDTO);
    }
  }

  close(res: MediumDTO | MediumGroupDTO) {
    this.modalNavigation.result(res);
  }

  cancel() {
    this.close(null);
  }
}
